import { gql } from "@/gql/__generated__";
import {
  Exact,
  Get_Organization_AppsQuery,
  Scalars
} from "@/gql/__generated__/graphql";
import { QueryResult, useQuery } from "@apollo/client";
import {
  OrganizationApp,
  SubscriptionApp
} from "../../features/organization-apps/types/app.type";

export const GET_ORGANIZATION_APPS = gql(`
  query GET_ORGANIZATION_APPS ($organizationSlug: String!) {
    organization(organizationSlug: $organizationSlug) {
      __typename
      ... on Organization {
      __typename
      id
      name
      apps {
        __typename
        id
        slug
        pictureUrl
        displayName
        description
        defaultDeployment {
          __typename
          id
          apiURL
          apiDocsPath
          current {
            __typename
            id
            createdAt
          }
        }
        subscription {
          __typename
          id
        }
        createdBy {
          fullName
        }
        createdAt
      }
      }
      ... on OrganizationNotFound {
        __typename
        id
      }
    }
  }
`);

type Result = QueryResult<
  Get_Organization_AppsQuery,
  Exact<{ organizationSlug: Scalars["String"]["input"] }>
>;
type GetOrganizationAppsOnlyResult = Omit<Result, "data"> & {
  data: OrganizationApp[];
};
type GetOrganizationAndSubscriptionAppsResult = Omit<Result, "data"> & {
  data: (OrganizationApp | SubscriptionApp)[];
};

type Props = {
  organizationSlug: string;
  organizationAppsOnly?: boolean;
};

// Function overload for better typesafety
function useGetOrganizationApps(props: {
  organizationSlug: string;
  organizationAppsOnly: true;
}): GetOrganizationAppsOnlyResult;
function useGetOrganizationApps(props: {
  organizationSlug: string;
  organizationAppsOnly?: false;
}): GetOrganizationAndSubscriptionAppsResult;
function useGetOrganizationApps({
  organizationSlug,
  organizationAppsOnly = false
}: Props):
  | GetOrganizationAppsOnlyResult
  | GetOrganizationAndSubscriptionAppsResult {
  const { data, ...query } = useQuery(GET_ORGANIZATION_APPS, {
    variables: { organizationSlug },
    skip: !organizationSlug,
    fetchPolicy: "cache-and-network"
  });

  let apps =
    data?.organization.__typename === "Organization"
      ? formatOrganizationApps(data.organization)
      : [];

  if (organizationAppsOnly) {
    apps = apps.filter((app) => app.origin === "organization");
  }

  return { data: apps, ...query };
}

function formatOrganizationApps(
  organization: Extract<
    Get_Organization_AppsQuery["organization"],
    { __typename: "Organization" }
  >
): (OrganizationApp | SubscriptionApp)[] {
  const apps: (OrganizationApp | SubscriptionApp)[] = organization.apps.map(
    (app) => ({
      ...app,
      origin:
        app.subscription?.__typename === "AppSubscription"
          ? "subscription"
          : "organization"
    })
  );

  return apps;
}

export { useGetOrganizationApps };
